/**
 * Il candidato in verifica: il visualizzatore, e cio' che gli si mette sopra.
 *
 * **Una regola sola, e regge tutto il componente: il fotogramma non si smonta.**
 * Conflitto di route, lease perduto, verdetto respinto — tutto arriva come un
 * velo *sopra* l'immagine, mai al posto suo. Un avviso che sostituisce il
 * visualizzatore costa il ricaricamento di decine di megapixel per una riga di
 * testo, e nel frattempo l'operatore perde di vista proprio cio' su cui stava
 * decidendo. Per questo il velo e' un fratello del visualizzatore e non un ramo
 * alternativo dell'albero: un ternario attorno a `<Viewer>` distruggerebbe il
 * nodo, e con lui la piramide caricata.
 *
 * **L'alternativa testuale dice il candidato, non «immagine».** Chi non vede il
 * fotogramma deve sapere di quale codice, di quale rango e di quale margine si
 * sta parlando.
 */
import type { ReactNode } from 'react'

import { margin as writeMargin, moment } from '../format'
import { useState } from 'react'

import { marginOf, rankOf } from '../design/scales'
import { useT } from '../i18n/LocaleProvider'
import type { Action } from '../keys'
import { indexPath } from '../pyramid'
import type { Box, Candidate, Frame, Point } from '../types'
import { Viewer, type FrameCard, type Mark, type Shape } from '../Viewer'
import { CanvasTools } from './CanvasTools'
import { CloudView, type CloudMode } from './CloudView'

export interface CandidateViewerProps {
  /** La commessa: serve a chiedere la nuvola del candidato (GI-241). */
  inspectionId?: string
  candidate: Candidate | null
  /**
   * Un fotogramma guardato al posto del candidato (GI-265): quello accanto nel
   * volo, senza un candidato in coda. Si mostra con le anomalie gia' registrate
   * sopra, e il pannello dice che qui non si decide.
   */
  look?: Frame | null
  /** Le anomalie gia' registrate sul fotogramma guardato, col loro tono. */
  marks?: Mark[]
  /** Che cosa si sta disegnando, o nulla. */
  drawing: Shape | null
  box: Box | null
  onDrawn: (box: Box) => void
  polygon: Point[] | null
  onDrawnPolygon: (points: Point[]) => void
  /** Il nome del modo in corso, nullo nel modo base. */
  mode: string | null
  hidden: boolean
  view: { how: 'uno-a-uno' | 'inquadra' | 'trovata'; nonce: number } | null
  /** Gli strumenti sulla tela mandano qui la stessa `Action` dei tasti. Senza,
   *  la palette non si monta (il visualizzatore fuori dal banco non ne ha). */
  onAction?: (action: Action) => void
  /**
   * Il velo: quel che copre l'immagine senza togliercela di sotto.
   *
   * Non e' un dialogo modale: l'operatore deve poter continuare a guardare, e
   * spesso e' guardando che capisce se il conflitto lo riguarda.
   */
  veil: ReactNode | null
  /** Che cosa si legge nella coda quando non c'e' niente da guardare. */
  empty: ReactNode
}

/** Come si nomina il candidato: codice, rango, margine. Le tre cose che si
 *  guardano insieme all'immagine, e che l'etichetta ripete sul fotogramma. */
export function nameOf(candidate: Candidate): string {
  return `${candidate.code} · ${rankOf(candidate.level).name} · Δ ${writeMargin(
    marginOf(candidate.measured_distance_m, candidate.minimum_distance_m),
  )}`
}

/** La scheda del fotogramma (§ 6.2, GI-266): la camera come bollino, il file,
 *  l'ora dello scatto, l'elemento. Cio' che manca non si scrive: «camera
 *  sconosciuta» sarebbe una riga in piu' per dire niente. */
export function cardOf(
  frame: {
    filename: string | null
    camera?: string | null
    taken_at?: string | null
  },
  element: { type?: string | null; id?: number | null } | null,
): FrameCard {
  const extra =
    element !== null && (element.type || element.id !== null)
      ? [element.type ?? '', element.id === null || element.id === undefined ? '' : String(element.id)]
          .filter(Boolean)
          .join(' ')
      : null
  return {
    camera: frame.camera ?? null,
    filename: frame.filename,
    when: frame.taken_at ? moment(frame.taken_at) : null,
    extra,
  }
}

export function CandidateViewer({
  inspectionId,
  candidate,
  look = null,
  marks = [],
  drawing,
  box,
  onDrawn,
  polygon,
  onDrawnPolygon,
  mode,
  hidden,
  view,
  onAction,
  veil,
  empty,
}: CandidateViewerProps): JSX.Element {
  const t = useT()
  const [vertices, setVertices] = useState(0)
  const framed =
    candidate !== null &&
    candidate.tiles_url !== null &&
    candidate.photo_width !== null &&
    candidate.photo_height !== null
  const looking = look !== null && look.tiles_url !== null && look.width !== null && look.height !== null
  // La nuvola c'e' solo per chi ne e' nato: una misura di franco si guarda
  // nei punti, non nel solo fotogramma (GI-241). Il modo torna al fotogramma
  // a ogni candidato: e' cio' che il resto del banco da' per scontato.
  const clouded = candidate !== null && candidate.source === 'lidar' && inspectionId !== undefined
  // Senza fotogramma il modo «foto» e' una pagina vuota: si apre la nuvola,
  // che per una misura di franco e' comunque la cosa da guardare.
  //
  // Lo sguardo scelto si ricorda **insieme al candidato a cui appartiene**, e
  // si deriva a ogni disegno: su un altro candidato torna quello predefinito
  // senza passare da un effetto. Un effetto che rimetteva lo sguardo dopo il
  // primo disegno lasciava, per un istante, «nuvola» premuto sotto un
  // candidato che aveva il fotogramma — e su una macchina lenta quell'istante
  // e' bastato a una prova per vederlo (CI, 8.9.2026).
  const [sight, setSight] = useState<{ of: string | null; look: 'photo' | CloudMode } | null>(null)
  const chosen =
    sight !== null && candidate !== null && sight.of === candidate.detection_id ? sight.look : null
  const showing: 'photo' | CloudMode = clouded ? (chosen ?? (framed ? 'photo' : 'cloud')) : 'photo'

  return (
    <section className="cockpit-tela" aria-label={t('cockpit.viewer')}>
      <h2 className="solo-lettori">{t('cockpit.viewer')}</h2>
      {onAction !== undefined && (candidate !== null || look !== null) && (
        <CanvasTools
          drawing={drawing}
          hidden={hidden}
          vertices={vertices}
          disabled={!(looking || (showing === 'photo' && framed))}
          // Su un fotogramma guardato senza candidato non c'e' un riquadro da
          // correggere ne' una trovata su cui tornare.
          drawable={!looking && framed}
          onAction={onAction}
        />
      )}
      {looking && look !== null && look.tiles_url !== null && (
        <Viewer
          tilesUrl={look.tiles_url}
          indexUrl={indexPath(look.photo_id)}
          width={look.width ?? 0}
          height={look.height ?? 0}
          tile={look.tile ?? 512}
          levels={look.levels ?? 1}
          box={null}
          marks={marks}
          mode={mode}
          hidden={hidden}
          view={view}
          frame={cardOf(look, null)}
          alt={t('cockpit.lookAlt', { filename: look.filename })}
        />
      )}
      {!looking && clouded && (
        <div className="cockpit-sguardi" role="group" aria-label={t('cloud.looks')}>
          {(['photo', 'cloud', 'profile'] as const).map((one) => (
            <button
              key={one}
              type="button"
              aria-pressed={showing === one}
              disabled={one === 'photo' && !framed}
              onClick={() => setSight({ of: candidate?.detection_id ?? null, look: one })}
            >
              {one === 'photo' ? t('cloud.lookPhoto') : one === 'cloud' ? t('cloud.lookCloud') : t('cloud.lookProfile')}
            </button>
          ))}
        </div>
      )}
      {!looking && clouded && showing !== 'photo' && candidate !== null && inspectionId !== undefined && (
        <CloudView inspectionId={inspectionId} detectionId={candidate.detection_id} mode={showing} />
      )}
      {!looking && candidate !== null && !clouded && (
        <p className="cockpit-senza-tela tenue">{t('cloud.unavailableForSource')}</p>
      )}
      {!looking && showing === 'photo' && framed && candidate.tiles_url !== null && (
        <Viewer
          tilesUrl={candidate.tiles_url}
          indexUrl={candidate.photo_id ? indexPath(candidate.photo_id) : null}
          width={candidate.photo_width ?? 0}
          height={candidate.photo_height ?? 0}
          tile={candidate.tile ?? 512}
          levels={candidate.levels ?? 1}
          box={box}
          drawing={drawing !== null}
          shape={drawing ?? 'box'}
          onDrawn={onDrawn}
          polygon={polygon}
          onDrawnPolygon={onDrawnPolygon}
          onDrawingProgress={setVertices}
          mode={mode}
          hidden={hidden}
          view={view}
          // Codice, rango e margine *sul riquadro*: sono le tre cose che si
          // guardano insieme all'immagine, e stanno dove sta lo sguardo —
          // cercarle in un angolo costa un movimento degli occhi per ogni
          // candidato (GI-266).
          boxLabel={nameOf(candidate)}
          // E in basso a sinistra la scheda: quale file, quale camera, quando,
          // su quale elemento (§ 6.2). Due scatti dello stesso palo si
          // confondono senza.
          frame={cardOf(
            {
              filename: candidate.photo_filename,
              camera: candidate.photo_camera ?? null,
              taken_at: candidate.photo_taken_at ?? null,
            },
            { type: candidate.element_type ?? null, id: candidate.element_external_id ?? null },
          )}
          alt={
            candidate.photo_filename === null
              ? t('cockpit.altFrame', { name: nameOf(candidate) })
              : t('cockpit.altFrameFile', {
                  name: nameOf(candidate),
                  filename: candidate.photo_filename,
                })
          }
        />
      )}
      {!looking && look !== null && (
        <p className="cockpit-senza-tela tenue">
          {t('cockpit.lookNoPyramid', { filename: look.filename })}
        </p>
      )}
      {look === null && showing === 'photo' && !framed && candidate !== null && (
        <p className="cockpit-senza-tela tenue">
          {clouded ? t('cloud.noFrameButCloud') : t('cockpit.noFrameDecide')}
        </p>
      )}
      {look === null && candidate === null && <div className="cockpit-senza-tela">{empty}</div>}
      {veil}
    </section>
  )
}
